/**
 * Connecting an ad account: a token and two ids.
 *
 * The same shape as `WhatsAppConnectForm` and the same three rules, which are
 * worth restating because the credential this one takes can spend money:
 *
 *   - **The token is never echoed, never pre-filled, and never offered back.**
 *     `type="password"`, `autoComplete="off"`, and no stored value is ever put
 *     in it — there is nothing on `MetaAdsIntegration` it could come from.
 *   - **It is cleared the moment the request is made**, success or failure, so
 *     it does not outlive its use. A retry means pasting it again, and the
 *     field says so.
 *   - **The two ids are kept on a validation failure.** Nothing left the
 *     browser, so the token has not been used yet; clearing it would punish a
 *     typo in an id field.
 *
 * ## THE FIELD DESCRIPTIONS ARE ONE LINE EACH, AND THE REST FOLDS
 *
 * This screen's rule is a figure, a chip, a label — and a form is the one place
 * instruction is allowed, because somebody is being asked to produce a specific
 * thing from another product's settings screen. One line says which thing.
 * Everything beyond that goes behind `MoreDetail`, where the page's disclosure
 * pattern already puts it.
 *
 * ## `act_` IS STRIPPED, NOT REFUSED
 *
 * Ads Manager shows `act_17843…` and that is what roughly half of all pastes
 * contain. `connectMetaAdsRequestSchema` preprocesses it away, so the field
 * accepts what Meta itself displays and stores the bare number. Refusing it
 * would be refusing a correct answer for being correctly copied.
 */
import { Button, FormField, Input } from '@wend/ui';
import { useState, type FormEvent } from 'react';

import { FormAlert } from '../../components/FormAlert';
import { ROOT_FIELD, apiFieldErrors } from '../../lib/api/errors';
import {
  connectMetaAds,
  connectMetaAdsRequestSchema,
  type MetaAdsCheckResponse,
  type MetaAdsIntegration,
} from '../../lib/api/meta-ads';
import { validate, withoutError, type FormErrors } from '../../lib/form';
import { MoreDetail } from '../templates/MoreDetail';
import { RequestFailureAlert, requestFailure, type RequestFailure } from './request-failure';

export function MetaAdsConnectForm({
  existing,
  onConnected,
  onCancel,
}: {
  existing: MetaAdsIntegration | null;
  onConnected: (response: MetaAdsCheckResponse) => void;
  onCancel?: () => void;
}) {
  const [systemUserToken, setSystemUserToken] = useState('');
  const [businessId, setBusinessId] = useState(existing?.businessId ?? '');
  const [adAccountId, setAdAccountId] = useState(existing?.adAccountId ?? '');
  const [errors, setErrors] = useState<FormErrors>({});
  const [failure, setFailure] = useState<RequestFailure | null>(null);
  const [pending, setPending] = useState(false);

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (pending) return;

    setFailure(null);

    const result = validate(connectMetaAdsRequestSchema, {
      systemUserToken,
      businessId,
      adAccountId,
    });
    if (!result.ok) {
      // Nothing left the browser, so the token has not been used and is kept.
      setErrors(result.errors);
      return;
    }

    setErrors({});
    setPending(true);

    let response: MetaAdsCheckResponse;
    try {
      response = await connectMetaAds(result.data);
    } catch (error) {
      const fieldErrors = apiFieldErrors(error);
      delete fieldErrors[ROOT_FIELD];
      setErrors(fieldErrors);
      setFailure(requestFailure(error));
      // SECURITY: the request was made, so the token has served its purpose and
      // does not outlive it.
      setSystemUserToken('');
      setPending(false);
      return;
    }

    setSystemUserToken('');
    onConnected(response);
  }

  return (
    <form
      data-role="meta-ads-connect-form"
      onSubmit={(event) => {
        void onSubmit(event);
      }}
      noValidate
      autoComplete="off"
      className="flex max-w-xl flex-col gap-5"
    >
      {failure !== null && <RequestFailureAlert failure={failure} />}
      {errors[ROOT_FIELD] !== undefined && <FormAlert tone="error">{errors[ROOT_FIELD]}</FormAlert>}

      <FormField
        label="System user token"
        required
        description="Encrypted before it is stored, and never shown again. It is cleared from this form when it is sent."
        error={errors.systemUserToken}
      >
        <Input
          // Never echoed, never pre-filled, and never offered a stored value.
          type="password"
          value={systemUserToken}
          onChange={(event) => {
            setSystemUserToken(event.target.value);
            setErrors((current) => withoutError(current, 'systemUserToken'));
          }}
          autoComplete="off"
          spellCheck={false}
          className="font-mono text-[0.9rem]"
          disabled={pending}
        />
      </FormField>

      <FormField
        label="Business Manager id"
        required
        description="From Business Settings — a long number."
        error={errors.businessId}
      >
        <Input
          value={businessId}
          onChange={(event) => {
            setBusinessId(event.target.value);
            setErrors((current) => withoutError(current, 'businessId'));
          }}
          autoComplete="off"
          spellCheck={false}
          inputMode="numeric"
          placeholder="1094423048392017"
          className="font-mono text-[0.9rem]"
          disabled={pending}
        />
      </FormField>

      <FormField
        label="Ad account id"
        required
        description="From Ads Manager. Paste it with or without act_."
        error={errors.adAccountId}
      >
        <Input
          value={adAccountId}
          onChange={(event) => {
            setAdAccountId(event.target.value);
            setErrors((current) => withoutError(current, 'adAccountId'));
          }}
          autoComplete="off"
          spellCheck={false}
          placeholder="act_178432094820193"
          className="font-mono text-[0.9rem]"
          disabled={pending}
        />
      </FormField>

      {/*
        Everything a person might need and most will not, one click away. The
        permissions line is the one that matters: `ads_read` alone connects
        perfectly well and cannot create, start or pause anything, and somebody
        who does not know that reads "Read only" on the card as a fault.
      */}
      <MoreDetail summary="Where these come from">
        <p>
          The token is a system user token from Business Settings → System users → Generate token,
          with the Wend app selected.
        </p>
        <p>
          Give it <span className="font-mono">ads_read</span> to see campaigns and spend, and{' '}
          <span className="font-mono">ads_management</span> as well to create, start and pause them.
          Meta grants the two separately, and a token carrying only the first connects and reports
          as read only.
        </p>
        <p>The ad account must be assigned to that system user in Business Settings.</p>
      </MoreDetail>

      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" size="sm" loading={pending}>
          {pending ? 'Asking Meta' : existing === null ? 'Connect and test' : 'Replace and test'}
        </Button>

        {onCancel !== undefined && (
          <Button type="button" variant="ghost" size="sm" disabled={pending} onClick={onCancel}>
            Cancel
          </Button>
        )}
      </div>
    </form>
  );
}
